"use client";

import type { IconButtonProps, SpanProps, Tokens } from "@chakra-ui/react";
import { ClientOnly, Skeleton, Span } from "@chakra-ui/react";
import type { ThemeProviderProps } from "next-themes";
import { ThemeProvider, useTheme } from "next-themes";
import * as React from "react";
import { LuMoon, LuSun } from "react-icons/lu";
import { IconButton } from "~/components/ui/icon-button";

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface ColorModeProviderProps extends ThemeProviderProps {}

/**
 * Where the light / system / dark preference is stored. The pre-paint script in
 * index.html reads the same key (and carries over the old "theme" key), so the
 * first paint and React agree.
 */
export const THEME_STORAGE_KEY = "vox.theme";

export function ColorModeProvider(props: ColorModeProviderProps) {
  // Both marks go on <html>: Chakra v3's `_dark` is `.dark &`, and the Ledger
  // token file switches on `[data-theme="dark"]`. "system" follows the OS live.
  // A theme switch is instant: the Ledger animates nothing for effect.
  return (
    <ThemeProvider
      attribute={["class", "data-theme"]}
      storageKey={THEME_STORAGE_KEY}
      defaultTheme="system"
      disableTransitionOnChange
      enableSystem
      enableColorScheme
      themes={["light", "dark", "system"]}
      {...props}
    />
  );
}

export type ColorMode = "light" | "dark";

export interface UseColorModeReturn {
  colorMode: ColorMode;
  setColorMode: (colorMode: ColorMode) => void;
  toggleColorMode: () => void;
}

export function useColorMode(): UseColorModeReturn {
  const { resolvedTheme, setTheme } = useTheme();
  const toggleColorMode = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };
  return {
    colorMode: resolvedTheme as ColorMode,
    setColorMode: setTheme,
    toggleColorMode,
  };
}

/**
 * A colour for a place that takes a CSS colour rather than a Chakra token (an
 * SVG `fill`, a `linear-gradient` stop). A CSS variable passes through; a
 * Chakra token name (`ok.softFg`, `chart1.fg`, `accent.solid`) becomes the
 * variable Chakra emits for it. The app keeps no colour ramps of its own, so
 * there is no hex to look up.
 */
export function getRawColorValue(color: string): string {
  if (color === "white" || color.startsWith("var(")) {
    return color;
  }

  const [colorName, ...rest] = color.split(".");
  if (!colorName || rest.length === 0) {
    return "currentColor";
  }

  const kebab = rest.join("-").replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
  return `var(--chakra-colors-${colorName}-${kebab})`;
}

export function useColorRawValue(variable: string): string {
  return getRawColorValue(variable);
}

export function useColorModeValue<T = Tokens["colors"]>(light: T, dark: T) {
  const { colorMode } = useColorMode();
  return colorMode === "dark" ? dark : light;
}

export function ColorModeIcon() {
  const { colorMode } = useColorMode();
  return colorMode === "dark" ? <LuMoon /> : <LuSun />;
}

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface ColorModeButtonProps extends Omit<IconButtonProps, "aria-label"> {}

export const ColorModeButton = React.forwardRef<
  HTMLButtonElement,
  ColorModeButtonProps
>(function ColorModeButton(props, ref) {
  const { toggleColorMode } = useColorMode();
  return (
    <ClientOnly fallback={<Skeleton boxSize="8" />}>
      <IconButton
        onClick={toggleColorMode}
        variant="ghost"
        aria-label="Toggle color mode"
        size="sm"
        ref={ref}
        {...props}
        css={{
          _icon: {
            width: "5",
            height: "5",
          },
        }}
      >
        <ColorModeIcon />
      </IconButton>
    </ClientOnly>
  );
});

export const LightMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function LightMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme light"
        colorPalette="light"
        ref={ref}
        {...props}
      />
    );
  },
);

export const DarkMode = React.forwardRef<HTMLSpanElement, SpanProps>(
  function DarkMode(props, ref) {
    return (
      <Span
        color="fg"
        display="contents"
        className="chakra-theme dark"
        colorPalette="dark"
        ref={ref}
        {...props}
      />
    );
  },
);
